import { FC, useEffect, useState, Dispatch, ReactElement, SetStateAction } from 'react';

import { readField } from '@eneco/cms-types';
import { maskHouseNumber } from '@eneco/common/maskInput/maskHouseNumber';
import { maskPostalcode } from '@eneco/common/maskInput/maskPostalcode';
import { ApiError } from '@eneco/dc/client/ApiError';
import { useFormatter, useTranslation } from '@eneco/i18n';
import { Box, InputSelect, InputText, Stack, Text, VisuallyHidden } from '@eneco/sparky';
import { SpinnerIcon } from '@eneco/sparky/icons';
import { Customers_Address_AddressModel } from '@monorepo-types/dc';
import { Controller, useFormContext, FieldValues, UseFormResetField } from 'react-hook-form';

import RichText from '../RichText/RichText';
import { TrackedNotificationBox } from '../TrackedNotificationBox/TrackedNotificationBox';

interface CustomFormField {
  value: {
    hint: string;
    label: string;
    placeholder?: string;
    requiredMessage: string;
    validationMessage: string;
  };
}

interface NotificationField {
  editable?: string;
  value: {
    content: string;
    title: string;
    variant: 'info' | 'success' | 'warning' | 'error' | 'chat';
  };
}

export const ADDRESS_FINDER_LOOKUP_ERROR_ID = 'address-finder-lookup-error';

export interface Error {
  status?: number;
}

/** A form field's texts, flat, as a component reading its content through the CMS layer has them. */
interface FormFieldLabels {
  hint?: string;
  label?: string;
  placeholder?: string;
  requiredMessage?: string;
  validationMessage?: string;
}

/** A notification's texts, flat. */
interface NotificationContent {
  content?: string;
  title?: string;
  variant?: 'info' | 'success' | 'warning' | 'error' | 'chat';
}

/**
 * The labels a caller hands over: flat, or still in Sitecore's field shape (`{ value: { ... } }`) for a caller that
 * has not moved to the CMS layer yet. Each is read through `readField`, so both shapes render the same.
 */
interface Labels {
  houseNumber?: CustomFormField | FormFieldLabels;
  houseNumberSuffix?: CustomFormField | FormFieldLabels;
  postalCode?: CustomFormField | FormFieldLabels;
  notFoundErrorNotification?: NotificationField | NotificationContent;
  serverErrorNotification?: NotificationField | NotificationContent;
}

type FoundAddressProps = {
  hasFoundAddressError: boolean;
  addressData: Customers_Address_AddressModel;
  addressFormatter: ReturnType<typeof useFormatter>['address'];
};

const FoundAddress: FC<FoundAddressProps> = ({ hasFoundAddressError, addressData, addressFormatter }) => {
  if (hasFoundAddressError) return null;

  return (
    <Box paddingTop="3" role="status">
      <Text size="BodyS">{addressFormatter.long(addressData)}</Text>
    </Box>
  );
};

interface Props {
  addressData: Customers_Address_AddressModel;
  apiError: ApiError;
  currentSelectedHouseNumberSuffix?: string;
  customLabels?: Labels;
  houseNumberSuffixOptions: {
    label: string;
    value: string;
  }[];
  isSuffixRequired: boolean;
  isValidating: boolean;
  setErrorData?: Dispatch<SetStateAction<ApiError | undefined>>;
  customErrorNotification?: ReactElement;
  hasAutocomplete: boolean;
  resetField: UseFormResetField<FieldValues>;
  addressGroupLabel: string;
}

export const AddressFinderView: FC<Props> = ({
  addressData,
  apiError,
  currentSelectedHouseNumberSuffix,
  customLabels,
  houseNumberSuffixOptions,
  isSuffixRequired,
  isValidating,
  setErrorData,
  customErrorNotification,
  hasAutocomplete,
  resetField,
  addressGroupLabel,
}) => {
  const { t } = useTranslation();
  const { address: addressFormatter } = useFormatter();
  const {
    control,
    formState: { errors: formError },
    register,
  } = useFormContext();

  const postalCodeLabels = readField<FormFieldLabels>(customLabels?.postalCode);
  const houseNumberLabels = readField<FormFieldLabels>(customLabels?.houseNumber);
  const houseNumberSuffixLabels = readField<FormFieldLabels>(customLabels?.houseNumberSuffix);

  let errorMessage: NotificationContent;

  switch (apiError?.status) {
    case 500:
      errorMessage = readField<NotificationContent>(customLabels?.serverErrorNotification) || {
        content: t(`validationMessage.value.serverError.text`),
        title: t(`validationMessage.value.serverError.title`),
        variant: 'error',
      };
      break;
    default:
      errorMessage = readField<NotificationContent>(customLabels?.notFoundErrorNotification) || {
        content: t(`validationMessage.value.notFoundError.text`),
        title: t(`validationMessage.value.notFoundError.title`),
        variant: 'error',
      };
      break;
  }

  // Tells screen reader users when the houseNumberSuffix select becomes interactive.
  const [suffixReadyMessage, setSuffixReadyMessage] = useState('');

  useEffect(() => {
    setSuffixReadyMessage(houseNumberSuffixOptions.length > 0 ? t('houseNumberSuffixReadyMessage.value') : '');
  }, [houseNumberSuffixOptions.length, t]);

  useEffect(() => {
    if (!setErrorData) return;

    if (apiError?.status) {
      setErrorData(apiError);
    } else {
      setErrorData(undefined);
    }
  }, [apiError]);

  const { street, city } = addressData;
  const hasFoundAddressError =
    Object.keys(formError).length > 0 ||
    !!apiError ||
    (!street && !city) ||
    (isSuffixRequired && !currentSelectedHouseNumberSuffix);

  return (
    <>
      <Box>
        <fieldset>
          <legend>
            <VisuallyHidden>{addressGroupLabel}</VisuallyHidden>
          </legend>
          <Stack direction={{ initial: 'column', md: 'row' }} gap="3">
            <Stack.Item grow>
              <Controller
                control={control}
                name="postalCode"
                render={({ field: { onChange, onBlur, name, value, ref } }) => (
                  <InputText
                    ref={ref}
                    error={
                      formError.postalCode?.message &&
                      (postalCodeLabels?.requiredMessage || t('postalCode.value.requiredMessage'))
                    }
                    hint={postalCodeLabels?.hint || t('postalCode.value.hint')}
                    label={postalCodeLabels?.label || t('postalCode.value.label')}
                    name={name}
                    onBlur={onBlur}
                    onChange={event => {
                      resetField('houseNumberSuffix', { defaultValue: '' });
                      onChange(maskPostalcode(event));
                    }}
                    placeholder={postalCodeLabels?.placeholder || t('postalCode.value.placeholder')}
                    value={value}
                    autoComplete={hasAutocomplete ? 'postal-code' : 'off'}
                  />
                )}
              />
            </Stack.Item>
            <Stack.Item grow>
              <Stack direction="row" gap="3">
                <Stack.Item grow>
                  <Controller
                    control={control}
                    name="houseNumber"
                    render={({ field: { onChange, onBlur, name, value, ref } }) => (
                      <InputText
                        ref={ref}
                        error={
                          formError.houseNumber?.message &&
                          (houseNumberLabels?.requiredMessage || t('houseNumber.value.requiredMessage'))
                        }
                        hint={houseNumberLabels?.hint || t('houseNumber.value.hint')}
                        label={houseNumberLabels?.label || t('houseNumber.value.label')}
                        name={name}
                        placeholder={houseNumberLabels?.placeholder || t('houseNumber.value.placeholder')}
                        onChange={event => {
                          resetField('houseNumberSuffix', { defaultValue: '' });
                          onChange(maskHouseNumber(event));
                        }}
                        value={value}
                        onBlur={onBlur}
                        inputMode="numeric"
                        autoComplete={hasAutocomplete ? undefined : 'off'}
                      />
                    )}
                  />
                </Stack.Item>
                <Stack.Item grow>
                  <InputSelect
                    {...register('houseNumberSuffix')}
                    data-testid="houseNumberSuffix"
                    error={
                      formError.houseNumberSuffix?.message &&
                      (houseNumberSuffixLabels?.requiredMessage || t('houseNumberSuffix.value.requiredMessage'))
                    }
                    hint={houseNumberSuffixLabels?.hint || t('houseNumberSuffix.value.hint')}
                    isDisabled={!houseNumberSuffixOptions.length}
                    label={houseNumberSuffixLabels?.label || t('houseNumberSuffix.value.label') || ''}
                    options={houseNumberSuffixOptions}
                    placeholder={' '}
                    autoComplete={hasAutocomplete ? undefined : 'off'}
                  />
                  <VisuallyHidden ariaLive="polite">{suffixReadyMessage}</VisuallyHidden>
                </Stack.Item>
              </Stack>
            </Stack.Item>
          </Stack>
        </fieldset>
      </Box>

      {apiError?.status ? (
        <div id={ADDRESS_FINDER_LOOKUP_ERROR_ID} data-testid={ADDRESS_FINDER_LOOKUP_ERROR_ID} tabIndex={-1}>
          <Box paddingTop="3">
            {customErrorNotification ?? (
              <TrackedNotificationBox
                emphasis="low"
                isAlert
                text={errorMessage?.content && <RichText html={errorMessage.content} />}
                title={errorMessage?.title}
                variant="error"
              />
            )}
          </Box>
        </div>
      ) : null}

      {isValidating ? (
        <Box paddingTop="3" role="status">
          <SpinnerIcon size="medium" />
          <VisuallyHidden>{t('loadingMessage.value')}</VisuallyHidden>
        </Box>
      ) : (
        <FoundAddress
          hasFoundAddressError={hasFoundAddressError}
          addressData={addressData}
          addressFormatter={addressFormatter}
        />
      )}
    </>
  );
};
